// Central source of truth for site information architecture.
// Every nav item, card grid, footer link and breadcrumb ultimately reads
// from these constants — no other file duplicates the data.

// -----------------------------------------------------------------------------
// SERVICES (4 categories × leaf services)
// URLs assume future routes; safe to hard-code because we'll build them next.
// -----------------------------------------------------------------------------
export const SERVICE_CATEGORIES = [
  {
    slug: "electrical-engineering",
    name: "Electrical",
    tagline: "Installation, panels, alarms & 24/7 breakdown.",
    leaves: [
      { slug: "industrial-electrical-installation", name: "Industrial Electrical Installation", blurb: "New installs, upgrades and rewires for factories, commercial and industrial sites." },
      { slug: "control-panel-design-and-build", name: "Control Panel Design & Build", blurb: "Bespoke control panels, wired and tested in-house to BS EN 61439." },
      { slug: "fire-alarms-and-emergency-lighting", name: "Fire Alarms & Emergency Lighting", blurb: "Design, install, service and cert to BS 5839 and BS 5266." },
      { slug: "24-7-emergency-breakdown-callout", name: "24/7 Emergency Breakdown Callout", blurb: "Round-the-clock rapid response for line-down electrical and mechanical failures." },
    ],
  },
  {
    slug: "mechanical-engineering",
    name: "Mechanical",
    tagline: "Machinery, refurbishment, fabrication & fluid systems.",
    leaves: [
      { slug: "machine-installation-and-commissioning", name: "Machine Installation & Commissioning", blurb: "New machinery installed, aligned, wired, commissioned and signed-off." },
      { slug: "machine-refurbishment", name: "Machine Refurbishment", blurb: "Extend the life of critical assets — strip, inspect, restore, recommission." },
      { slug: "bespoke-machinery-design-and-build", name: "Bespoke Machinery Design & Build", blurb: "Purpose-built production and process machinery, designed and manufactured in-house." },
      { slug: "welding-and-fabrication", name: "Welding & Fabrication", blurb: "MIG, TIG and stick welding plus general steel fabrication for industrial jobs." },
      { slug: "hydraulics-and-pneumatics", name: "Hydraulics & Pneumatics", blurb: "Diagnostics, repairs and full system installs for industrial fluid power." },
      { slug: "water-systems-and-pipework", name: "Water Systems & Pipework", blurb: "Industrial pipework, pumps and process water for manufacturing and utility sites." },
    ],
  },
  {
    slug: "controls-and-automation",
    name: "Controls & Automation",
    tagline: "PLC, HMI, SCADA and full systems integration.",
    leaves: [
      { slug: "plc-programming", name: "PLC Programming", blurb: "Ladder, structured text and function-block programming — new builds, upgrades and fault-finding across the major PLC platforms." },
      { slug: "industrial-automation", name: "Industrial Automation", blurb: "End-to-end automation projects from concept and specification to build and commission." },
      { slug: "hmi-and-scada-development", name: "HMI & SCADA Development", blurb: "Operator interfaces and plant-wide supervisory systems, built for real users." },
    ],
  },
  {
    slug: "compliance-and-testing",
    name: "Compliance & Testing",
    tagline: "EICR, thermal, PAT and planned maintenance.",
    leaves: [
      { slug: "eicr-and-fixed-wire-testing", name: "EICR & Fixed Wire Testing", blurb: "18th Edition inspections, remedials and certification for industrial and commercial sites." },
      { slug: "thermal-imaging-and-condition-monitoring", name: "Thermal Imaging & Condition Monitoring", blurb: "Non-invasive surveys of switchgear, motors and mechanical assets — catch faults early." },
      { slug: "pat-testing", name: "PAT Testing", blurb: "Portable appliance testing for commercial estates and industrial sites, with full asset logs." },
      { slug: "planned-preventive-maintenance", name: "Planned Preventive Maintenance", blurb: "Scheduled PPMs across mechanical, electrical and controls — priced per site." },
    ],
  },
];

// Flat list of all service leaves — used by footer, sitemap, Related blocks.
export const ALL_SERVICES = SERVICE_CATEGORIES.flatMap((c) =>
  c.leaves.map((l) => ({ ...l, category: c.slug, categoryName: c.name }))
);

// -----------------------------------------------------------------------------
// INDUSTRIES
// -----------------------------------------------------------------------------
export const INDUSTRIES = [
  { slug: "manufacturing", name: "Manufacturing & Production", blurb: "Uptime, compliance and continuous improvement for production lines." },
  { slug: "food-and-beverage", name: "Food & Beverage", blurb: "IP-rated washdown, BRC-audit-ready installs and 24/7 line support." },
  { slug: "pharmaceutical", name: "Pharmaceutical & Life Sciences", blurb: "GMP-aware engineering for regulated environments and clean utilities." },
  { slug: "logistics-and-warehousing", name: "Logistics & Warehousing", blurb: "Conveyor systems, sortation, PPM and rapid response for distribution centres." },
  { slug: "education-and-schools", name: "Education & Schools", blurb: "EICRs, fire alarm servicing and electrical works for schools, colleges and universities." },
  { slug: "sport-and-leisure", name: "Sport & Leisure", blurb: "Stadium electrical, LED floodlighting and back-of-house engineering." },
  { slug: "retail-and-commercial", name: "Retail & Commercial", blurb: "Multi-site rolling maintenance and one-off installs for commercial estates." },
];

// -----------------------------------------------------------------------------
// COVERAGE — regions (v1) and towns (v1 subset).
// -----------------------------------------------------------------------------
export const REGIONS = [
  { slug: "cheshire", name: "Cheshire", blurb: "Same-day cover for Warrington, Chester, Crewe, Macclesfield and beyond." },
  { slug: "greater-manchester", name: "Greater Manchester", blurb: "Manchester, Salford, Bolton and Stockport — usually on-site within the hour." },
  { slug: "merseyside", name: "Merseyside", blurb: "Liverpool, St Helens, Wirral and Southport — including 24/7 breakdown cover." },
  { slug: "lancashire", name: "Lancashire", blurb: "Preston, Blackburn, Blackpool, Burnley and the wider Lancashire industrial base." },
  { slug: "north-west-england", name: "North West England", blurb: "Full regional coverage — one team, one number, whichever county you're in." },
];

export const TOWNS = [
  { slug: "manchester", name: "Manchester", region: "greater-manchester" },
  { slug: "liverpool", name: "Liverpool", region: "merseyside" },
  { slug: "warrington", name: "Warrington", region: "cheshire" },
  { slug: "chester", name: "Chester", region: "cheshire" },
  { slug: "preston", name: "Preston", region: "lancashire" },
  { slug: "salford", name: "Salford", region: "greater-manchester" },
  { slug: "bolton", name: "Bolton", region: "greater-manchester" },
  { slug: "st-helens", name: "St Helens", region: "merseyside" },
  { slug: "wirral", name: "Wirral", region: "merseyside" },
  { slug: "crewe", name: "Crewe", region: "cheshire" },
  { slug: "macclesfield", name: "Macclesfield", region: "cheshire" },
  { slug: "blackburn", name: "Blackburn", region: "lancashire" },
];

// -----------------------------------------------------------------------------
// COMPANY / ABOUT SUB-NAV
// -----------------------------------------------------------------------------
export const ABOUT_LINKS = [
  { slug: "", name: "Our Story", desc: "How and why Lutrom was founded." },
  { slug: "team", name: "The Team", desc: "Meet the engineers behind the work." },
  { slug: "quality-and-compliance", name: "Quality & Compliance", desc: "ISO 9001 / 14001 / 45001 detail." },
  { slug: "accreditations", name: "Accreditations", desc: "SafeContractor, IPAF, City & Guilds." },
  { slug: "careers", name: "Careers", desc: "Roles and life at Lutrom." },
];

// -----------------------------------------------------------------------------
// LEGAL / UTILITY
// -----------------------------------------------------------------------------
export const LEGAL_LINKS = [
  { path: "/privacy-policy", name: "Privacy Policy" },
  { path: "/cookie-policy", name: "Cookie Policy" },
  { path: "/terms-and-conditions", name: "Terms & Conditions" },
  { path: "/accessibility-statement", name: "Accessibility" },
];

// -----------------------------------------------------------------------------
// TOP-LEVEL NAV — desktop bar + mobile menu
// -----------------------------------------------------------------------------
export const PRIMARY_NAV = [
  { path: "/services", label: "Services", type: "mega", key: "services" },
  { path: "/industries", label: "Industries", type: "mega", key: "industries" },
  { path: "/coverage", label: "Coverage", type: "mega", key: "coverage" },
  { path: "/case-studies", label: "Case Studies", type: "link" },
  { path: "/insights", label: "Insights", type: "link" },
  { path: "/about", label: "About", type: "mega", key: "about" },
];
